import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Application } from "../../services/soap/features/responses/ListApplicationsResponse";

export interface AppInfo {
    key: string;
    data: any;
}

export interface FormSignature {
    ident?: string;
    name: string;
    picture: string;
}

interface UserWebLayoutState {
    appInfos: any;
    selectedAppId: string;
    selectedApp: Application | null;
    formSignatures: { [key: string]: FormSignature | undefined };
}

const initialState: UserWebLayoutState = {
    appInfos: {},
    selectedAppId: '',
    selectedApp: null,
    formSignatures: {},
};

// Helper function to ensure data is serializable
const ensureSerializable = (data: any): any => {
    if (data === null || data === undefined) {
        return data;
    }
    // If it's already a primitive or simple value, return as-is
    if (typeof data !== 'object') {
        return data;
    }
    // Parse and stringify to remove class instances and circular references
    try {
        return JSON.parse(JSON.stringify(data));
    } catch (error) {
        console.warn('Failed to serialize data, returning empty object:', error);
        return {};
    }
};

const userWebLayoutSlice = createSlice({
    name: 'userWebLayout',
    initialState,
    reducers: {
        addAppInfo(state, action: PayloadAction<string>) {
            state.appInfos[action.payload] = { };
        },
        removeAppInfo(state, action: PayloadAction<string>) {
            state.appInfos[action.payload] = undefined;
        },
        updateAppInfo(state, action: PayloadAction<AppInfo>) {
            // Ensure the data being stored is serializable
            state.appInfos[action.payload.key] = ensureSerializable(action.payload.data);
        },
        selectApp(state, action: PayloadAction<string | Application>) {
            if (typeof action.payload === 'string') {
                // Backward compatibility: if string is passed, store as ID only
                state.selectedAppId = action.payload;
                state.selectedApp = null;
            } else {
                // If Application object is passed, store both ID and full object
                state.selectedAppId = action.payload.id;
                state.selectedApp = ensureSerializable(action.payload) as Application;
            }
        },
        clearAllAppInfos(state) {
            state.appInfos = {};
        },
        addFormSignature(state, action: PayloadAction<FormSignature>) {
            if (action.payload.ident) {
                state.formSignatures[action.payload.ident] = {
                    name: action.payload.name,
                    picture: action.payload.picture
                };
            }
        },
        removeFormSignature(state, action: PayloadAction<string>) {
            state.formSignatures[action.payload] = undefined;
        },
        clearFormSignatures(state) {
            state.formSignatures = {};
        },
    },
});

export const { 
    addAppInfo, 
    removeAppInfo,
    updateAppInfo,
    selectApp,
    clearAllAppInfos,
    addFormSignature,
    removeFormSignature,
    clearFormSignatures
} = userWebLayoutSlice.actions;

export default userWebLayoutSlice.reducer;
